// Import 3rd-party dependencies
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

// Import project-specific dependencies
import { ApiService } from '@app/services/api.service';
import { environment } from '@app/environments/environment';
import { IFavoriteLink } from '@app/services/favorites/favorites.service';

export interface IProfileSettings {
  id: string;
  personas: [];
  favorites: [];
  interests: [];
}

export interface ITimeZoneLocale {
  city: string;
  province: string;
  country: string;
  timezone: string;
}

export interface IClockSettings {
  dateFormat: string;
  timeFormat: string;
  favorites: ITimeZoneLocale[];
  recent: ITimeZoneLocale[];
  current: ITimeZoneLocale;
  showDayOfWeek: boolean;
}

export interface IClockSettingsDocument {
  worldclock: IClockSettings;
}

export interface IRecents {
  searches: string[];
}

export interface IWorkdayProfile {
  employeeId: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  isManager: boolean;
  coreId: string;
}

export interface DdUser {
  core_id: string;
  [key: string]: string;
}

export interface IEMSProfile {
  value: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserProfileService extends ApiService {
  private PROFILE_API_URL = environment.DEPS.PROFILE_API.URL;
  private GRAPH_API_URL = `${environment.GRAPH.URL}/${environment.GRAPH.VERSIONS.V1}`;

  profile$ = this.get<IProfileSettings>(`${this.PROFILE_API_URL}/me`);
  preferences$ = this.get<IClockSettingsDocument>(`${this.PROFILE_API_URL}/me/prefs`);
  resourceLinks$ = this.get<IFavoriteLink>(`${this.PROFILE_API_URL}/me/resourcelinks`);
  recents$ = this.get<IRecents>(`${this.PROFILE_API_URL}/me/recents`);

  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  addUserFavorites(data: string[]): Observable<void> {
    return this.put(`${this.PROFILE_API_URL}/me/favorites`, data);
  }

  addUserPersonas(data: string[]): Observable<void> {
    return this.put(`${this.PROFILE_API_URL}/me/personas`, data);
  }

  addUserInterests(data: string[]): Observable<void> {
    return this.put<void>(`${this.PROFILE_API_URL}/me/interests`, data);
  }

  addClockSettings(data: IClockSettingsDocument): Observable<void> {
    return this.put(`${this.PROFILE_API_URL}/me/prefs`, data);
  }

  saveSearches(searches: string[]): Observable<void> {
    return this.post(`${this.PROFILE_API_URL}/me/recents`, { searches });
  }

  updateSearches(searches: string[]): Observable<void> {
    return this.put(`${this.PROFILE_API_URL}/me/recents`, { searches });
  }

  getDdProfile(coreId: string): Observable<DdUser> {
    return this.get(`${this.PROFILE_API_URL}/ddprofiles/${coreId}`);
  }

  getWorkdayProfile(id: string): Observable<IWorkdayProfile> {
    return this.get(`${this.PROFILE_API_URL}/workday/${id}`);
  }

  checkEMSmembership(groupId: string): Observable<IEMSProfile> {
    return this.post(`${this.GRAPH_API_URL}/me/checkMemberGroups`, { groupIds: [groupId] });
  }
}
